import React from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';
import SEOHead from '../../components/SEOHead';
import { 
  FaCompass, 
  FaMagic,
  FaExclamationTriangle,
  FaChartLine,
  FaHeart,
  FaPhoneAlt,
  FaShieldAlt,
  FaStethoscope,
  FaCogs,
  FaRoute,
  FaNetworkWired,
  FaSync
} from 'react-icons/fa';
import { GiBroadsword } from "react-icons/gi";
import { GiRadarSweep } from 'react-icons/gi';
import { FaTimeline } from "react-icons/fa6";
import veloxIcon from '../../assets/Velox-icon-b&w.png';
import '../../styles/FeaturesPage.css';

export default function FeaturesPage() {
  return (
    <PublicLayout>
      <SEOHead 
        title="Features - AI-Powered Sales Intelligence Modules"
        description="Discover RADAR, COMPASS, WIZARD, and Agent V - four AI modules that transform deal chaos into clarity. Ambient intelligence for modern sales teams."
        url="/features"
      />
      {/* Hero Section */}
      <section className="features-hero">
        <div className="container">
          <h1>Ambient Intelligence. Wherever You Need It.</h1>
          <p>Velox AI isn’t just another sales tool—it’s the backbone of clarity in complex B2B pipelines. The modules work invisibly, weaving insights, context, and guidance into every interaction, workflow, and decision. No noise. Just intelligence.</p>
        </div>
      </section>

      {/* RADAR Module */}
      <section className="feature-section" id="radar">
        <div className="container">
          <div className="feature-layout">
            <div className="feature-content-left">
              <div style={{textAlign: 'top', gap: '10px'}} className="feature-badge">
                <span style={{fontSize: '24px'}}><GiRadarSweep /></span>
                <h3 style={{fontSize: '28px', color: '#3b82f6'}}>RADAR</h3>
              </div>
              <p className="feature-description" style={{fontSize: '16px'}}>
                Continuous deal monitoring powered by transcript analysis and signal detection — highlighting risks and opportunities before they escalate.
              </p>
              
              <div className="feature-details">
                <div className="feature-item">
                  <div className="feature-icon"><FaChartLine /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Signal Detection</h3>
                    <p style={{fontSize: '14px'}}>Flags early warning signs like stalled engagement, weak champion buy-in, or lack of urgency</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon"><FaHeart /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Sentiment Analysis</h3>
                    <p style={{fontSize: '14px'}}>Understands buyer tone and intent across calls to separate interest from hesitation</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon"><FaExclamationTriangle /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Risk Alerts</h3>
                    <p style={{fontSize: '14px'}}>Highlights critical risks like budget constraints, technical roadblocks, or regulatory delays</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="feature-animation-right">
              <div className="radar-animation">
                <div className="radar-circle radar-circle-1"></div>
                <div className="radar-circle radar-circle-2"></div>
                <div className="radar-circle radar-circle-3"></div>
                <div className="radar-circle radar-circle-4"></div>
                {/* Radial lines */}
                <div className="radar-radial-lines">
                  {[...Array(8)].map((_, i) => (
                    <div 
                      key={i} 
                      className="radar-radial-line"
                      style={{
                        transform: `rotate(${i * 45}deg)`
                      }}
                    />
                  ))}
                </div>
                
                {/* Tick marks */}
                <div className="radar-ticks">
                  {[...Array(24)].map((_, i) => (
                    <div 
                      key={i} 
                      className="radar-tick"
                      style={{
                        transform: `rotate(${i * 15}deg) translateY(-180px)`
                      }}
                    />
                  ))}
                </div>
                <div className="radar-line"></div>
                <div className="radar-center">
                  <GiRadarSweep className="radar-icon" />
                </div>
                <div className="radar-pulse"></div>
                <div className="radar-dots">
                  {[...Array(10)].map((_, i) => (
                    <div 
                      key={i} 
                      className="radar-dot"
                      style={{
                        left: `${Math.random() * 80 + 10}%`,
                        top: `${Math.random() * 80 + 10}%`,
                        animationDelay: `${Math.random() * 3}s`,
                        animationDuration: `${1 + Math.random() * 2}s`
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPASS Module */}
      <section className="feature-section" id="compass">
        <div className="container">
          <div className="feature-layout compass-layout">
            <div className="feature-animation-left">
              <div className="compass-animation">
                <div className="compass-ring compass-ring-1"></div>
                <div className="compass-ring compass-ring-2"></div>
                <div className="compass-ring compass-ring-3"></div>
                <div className="compass-needle">
                  <div className="needle-bottom"></div>
                </div>
                <div className="compass-center">
                  <FaCompass className="compass-icon" />
                </div>
                <div className="compass-glow"></div>
                <div className="compass-points">
                  {['N', 'E', 'S', 'W'].map((direction, i) => (
                    <div 
                      key={direction} 
                      className="compass-point"
                      style={{
                        transform: `rotate(${i * 90}deg) translateY(-140px)`,
                        animationDelay: `${i * 0.3}s`
                      }}
                    >
                      {direction}
                    </div>
                  ))}
                </div>
                <div className="compass-rotation">
                  {[...Array(12)].map((_, i) => (
                    <div 
                      key={i} 
                      className="compass-tick"
                      style={{
                        transform: `rotate(${i * 30}deg) translateY(-130px)`,
                        animationDelay: `${i * 0.1}s`
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="feature-content-right">
              <div style={{textAlign: 'top', gap: '10px'}} className="feature-badge">
                <span style={{fontSize: '24px'}}><FaCompass /></span>
                <h3 style={{fontSize: '28px'}}>COMPASS</h3>
              </div>
              <p className="feature-description" style={{fontSize: '16px'}}>
                COMPASS generates highly contextual, multi-format sales content to equip reps for every stage of the deal and every stakeholder they engage with.
              </p>
              
              <div className="feature-details">
                <div className="feature-item">
                  <div className="feature-icon"><FaPhoneAlt /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Call Preparation</h3>
                    <p style={{fontSize: '14px'}}>Auto-generate account briefs, pre-call summaries, and demo checklists to cut prep time in half</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon"><GiBroadsword /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Competitive Intelligence</h3>
                    <p style={{fontSize: '14px'}}>Deliver dynamic battle cards, ROI comparisons, and TCO analyses that position you as the clear winner</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon"><FaShieldAlt /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Objection Handling & Proposals</h3>
                    <p style={{fontSize: '14px'}}>Provide stakeholder-specific objection responses and create executive-ready proposals and roadmaps in minutes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WIZARD Module */}
      <section className="feature-section" id="wizard">
        <div className="container">
          <div className="feature-layout wizard-layout">
            <div className="feature-content-left">
              <div style={{textAlign: 'top', gap: '10px'}} className="feature-badge">
                <span style={{fontSize: '24px'}}><FaMagic /></span>
                <h3 style={{fontSize: '28px'}}>WIZARD</h3>
              </div>
              <p className="feature-description" style={{fontSize: '16px'}}>
                WIZARD transforms deal chaos into clarity by guiding reps with the right workflows and diagnostics to move opportunities forward.
              </p>
              
              <div className="feature-details">
                <div className="feature-item">
                  <div className="feature-icon"><FaStethoscope /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Deal Diagnostics</h3>
                    <p style={{fontSize: '14px'}}>Evaluates pipeline health to reveal what's strong, what's at risk, and what needs action</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon"><FaCogs /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Smart Workflows</h3>
                    <p style={{fontSize: '14px'}}>Automates playbooks and removes guesswork, helping teams execute consistently</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon"><FaRoute /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Next Best Action</h3>
                    <p style={{fontSize: '14px'}}>Recommends the most effective step for every opportunity to maintain deal velocity</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="feature-animation-right">
              <div className="wizard-animation">
                <div className="magic-wand">
                  <FaMagic className="wizard-icon" />
                </div>
                
                <div className="magic-sparks">
                  {[...Array(15)].map((_, i) => (
                    <div 
                      key={i} 
                      className="spark"
                      style={{
                        left: `${Math.random() * 60 + 20}%`,
                        top: `${Math.random() * 60 + 20}%`,
                        animationDelay: `${Math.random() * 2}s`,
                        animationDuration: `${1 + Math.random() * 2}s`
                      }}
                    ></div>
                  ))}
                </div>
                
                <div className="magic-particles">
                  {[...Array(12)].map((_, i) => (
                    <div 
                      key={i} 
                      className="particle"
                      style={{
                        left: `${Math.random() * 80 + 10}%`,
                        top: `${Math.random() * 80 + 10}%`,
                        animationDelay: `${Math.random() * 3}s`,
                        animationDuration: `${2 + Math.random() * 2}s`
                      }}
                    ></div>
                  ))}
                </div>
                
                <div className="magic-stars">
                  {[...Array(8)].map((_, i) => (
                    <div 
                      key={i} 
                      className="star"
                      style={{
                        left: `${Math.random() * 70 + 15}%`,
                        top: `${Math.random() * 70 + 15}%`,
                        animationDelay: `${Math.random() * 2}s`
                      }}
                    ></div>
                  ))}
                </div>
                <div className="wizard-aura"></div>
              </div>
            </div>
          </div>
        </div>
      </section>   

      {/* Agent V Module */}
      <section className="feature-section" id="agent-v">
        <div className="container">
          <div className="feature-layout agent-v-layout">
            <div className="feature-animation-left">
              <div className="agent-v-animation">
                <div className="orb-outer-ring"></div>
                <div className="orb-center">
                  <img src={veloxIcon} alt="Agent V" className="agent-v-icon" />
                </div>
                <div className="orb-aura"></div>
              </div>
            </div>
            
            <div className="feature-content-right">
              <div style={{textAlign: 'top', gap: '10px'}} className="feature-badge">
                <span style={{fontSize: '24px', height: '60px', width: '60px', padding: '15px', borderRadius: '8px', filter: 'brightness(0) invert(1)'}}><img src={veloxIcon} alt="Agent V" className="module-icon-image" /></span>
                <h3 style={{fontSize: '28px'}}>Agent V</h3>
              </div>
              <p className="feature-description" style={{fontSize: '16px'}}>
              AGENT V is the orchestrator that powers the entire Velox ecosystem. It integrates intelligence seamlessly into the tools your team already uses — Slack, CRM, email — ensuring insights are ambient, not disruptive.
              </p>
              
              <div className="feature-details">
                <div className="feature-item">
                  <div className="feature-icon"><FaNetworkWired /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Orchestration Engine</h3>
                    <p style={{fontSize: '14px'}}>Unifies COMPASS, RADAR, and WIZARD into a single intelligent backbone</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon"><FaTimeline /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Contextual Delivery</h3>
                    <p style={{fontSize: '14px'}}>Pushes insights to the right person, at the right time, inside their workflow</p>
                  </div>
                </div>
                
                <div className="feature-item">
                  <div className="feature-icon"><FaSync /></div>
                  <div className="feature-text">
                    <h3 style={{fontSize: '18px'}}>Adaptive Learning</h3>
                    <p style={{fontSize: '14px'}}>Continuously evolves with your sales process, making intelligence sharper over time</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>    


      {/* Integration Section */}
      <section className="integration-section">
        <div className="container">
          <h2>Seamless Integration with Your Tech Stack</h2>
          <p>Velox AI integrates with your existing tools to provide a unified revenue operations enablement experience</p>
          
          <div className="integration-logos">
            <div className="logo-item">HubSpot</div>
            <div className="logo-item">Slack</div>
            <div className="logo-item">Google Docs</div>
          </div>

          <p style={{fontSize: '16px', color: 'gray'}}>Many more integrations coming soon</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="cta-background">
          <div className="cta-gradient"></div>
          <div className="cta-particles"></div>
        </div>
        
        <div className="container">
          <h2>Ready to experience Velox AI?</h2>
          <p>Start with our Free Plan today — no credit card required</p>
          
          <div className="cta-buttons">
            <Link to="/waitlist" className="btn btn-primary btn-large">Create Account</Link>
            <Link to="/pricing" className="btn btn-secondary">View Pricing</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
} 